<script setup lang="ts">
import { ref, computed, watch, onUnmounted, type PropType } from 'vue'
import { useStore } from '@nanostores/vue'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import {
	getMercariUrl,
	getFormattedPrice,
	getMercariStatus,
} from '@/lib/mercari'
import GallerySlideshow from '@/legacy/components/GallerySlideshow.vue'
import {
	fetchMercariProductDetail,
	buildCategoryBreadcrumbs,
} from '@/assets/ts/apiManagement'
import {
	type IMercariProductDetail,
	MercariProductTypeEnum,
	TranslateMode,
	ContentApiMercariProductDetail,
} from '@/types/Mercari'
import { type IBreadcrumbItem } from '@/types/BreadCrumb'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import MercariDetailProductName from '@/pages/mercari-detail/components/_MercariDetailProductName.vue'
import MercariDetailTag from '@/pages/mercari-detail/components/_MercariDetailTag.vue'
import MercariDetailTranslation from '@/pages/mercari-detail/components/_MercariDetailTranslation.vue'
import MercariDetailInfo from '@/pages/mercari-detail/components/_MercariDetailInfo.vue'
import MercariDetailSeller from '@/pages/mercari-detail/components/_MercariDetailSeller.vue'
import MercariDetailShoppingDetail from '@/pages/mercari-detail/components/_MercariDetailShoppingDetail.vue'
import MercariDetailCategoryBreadcrumb from '@/components/ui/BaseBreadcrumb.vue'
import {
	Name as WebComponentNameList,
	createCheckoutShippingHowItWorksView,
} from '@/legacy/ts/webComponents'
import DialogFull from '@/legacy/components/DialogFull.vue'
import HowItWorksSkeleton from '@/legacy/components/HowItWorksSkeleton.vue'
import { getSiteBaseUrl, getMcrRootUrl } from '@/lib/siteBaseUrl'
import { isLogin } from '@/store/userStore'
import DialogRevamp from '@/components/DialogRevamp.vue'
import {
	isRegionSupportBuyforyou,
	checkIsSeoEnhancement,
} from '@/lib/checkService'
import Skeleton from '@/components/Skeleton.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import getSeoRedirectLink from '@/lib/getSeoRedirect'

const props = defineProps({
	pageContent: {
		type: Object as PropType<{
			[key: string]: string
		}>,
		required: true,
	},
})

const isLoginGlobal = useStore(isLogin)
const { isSmall } = useBreakpoints()
const isLoading = ref(true)
const isShowDialogHowItWork = ref(false)
const isWebComponentLoading = ref(true)
const dataMercariDetail = ref<IMercariProductDetail | null>(null)
const isShowLocalLanguage = ref(false)
const dialogHowItWorkTitle = ref('')
const isShowDialogFeeDetails = ref(false)
const isServerError = ref(false)
const breadcrumbs = ref<IBreadcrumbItem[]>([])
const isLoadingBreadcrumbs = ref(false)
const hasTranslationData = ref(false)
const translationTimer = ref<NodeJS.Timeout | null>(null)
const isTranslateSectionShow = computed(() => {
	return hasTranslationData.value
})

const isDisplayLocalLanguage = computed(() => {
	return isTranslateSectionShow.value && isShowLocalLanguage.value
})
const finalPriceWithCurrency = computed(() => {
	return getFormattedPrice(
		dataMercariDetail.value?.productInfo.price || 0,
		dataMercariDetail.value?.productInfo.currency || 'JPY',
	)
})

const listShoppingDetail = [
	{
		icon: 'shopping_plane',
		text: props.pageContent.detail_shopping_details_item_title_process,
		action: () => {
			showDialogHowItWork()
		},
	},
	{
		icon: 'shopping_protection',
		text: props.pageContent.detail_shopping_details_item_title_protection,
		action: () => {
			redirectShipmentProtection()
		},
	},
]

const originalPriceWithCurrency = computed(() => {
	if (!dataMercariDetail.value?.productInfo.originalPrice) {
		return null
	}
	return getFormattedPrice(
		dataMercariDetail.value?.productInfo.originalPrice,
		String(dataMercariDetail.value?.productInfo.currency),
	)
})

const hasPromotions = computed(() => {
	const productInfo = dataMercariDetail.value?.productInfo ?? null
	if (!productInfo) return false

	const originalPrice = productInfo.originalPrice ?? null
	const price = productInfo.price ?? null
	if (originalPrice === null || price === null) return false

	return price < originalPrice
})

const currentDetailType = computed(() => {
	return getMercariStatus({
		isBuyaholicProduct: false,
		status: dataMercariDetail.value?.productInfo.status || 'on_sale',
		discountPercentage:
			dataMercariDetail.value?.productInfo.discountTotalReturnPercent || 0,
		hasPromotions: hasPromotions.value,
	})
})

const tagText = computed(() => {
	switch (currentDetailType.value) {
		case MercariProductTypeEnum.DISCOUNT:
			return `${props.pageContent.detail_tag_discount.replace(
				'{discount}',
				String(
					dataMercariDetail.value?.productInfo.discountTotalReturnPercent || '',
				),
			)}`
		case MercariProductTypeEnum.SALE:
			return `${props.pageContent.detail_tag_on_sale}`
		case MercariProductTypeEnum.SOLD_OUT:
			return ''
		default:
			return ''
	}
})

const isShowTag = computed(() => {
	if (isLoading.value) {
		return true
	}
	return (
		currentDetailType.value !== MercariProductTypeEnum.NORMAL &&
		currentDetailType.value !== MercariProductTypeEnum.SOLD_OUT
	)
})

const mediaList = computed(() => {
	if (!dataMercariDetail.value) {
		return []
	}
	return dataMercariDetail.value.productInfo.images.map(image => ({
		type: 'image',
		content: image,
	}))
})

const currentTranslateSectionContent = computed(() => {
	return {
		title: isDisplayLocalLanguage.value
			? props.pageContent.detail_translate_desc_translated
			: props.pageContent.detail_translate_desc_original,
		button: isDisplayLocalLanguage.value
			? props.pageContent.detail_translate_button_translated
			: props.pageContent.detail_translate_button_original,
	}
})

const contentShippingDays = computed(() => {
	const shippingMinDay =
		dataMercariDetail.value?.productInfo.shippingDurationMinDay
	const shippingMaxDay =
		dataMercariDetail.value?.productInfo.shippingDurationMaxDay

	if (!shippingMaxDay && !shippingMinDay) {
		return ''
	}

	if (shippingMinDay && shippingMaxDay) {
		return `${props.pageContent.detail_item_estimated_shipping_date
			.replace('{minDay}', String(shippingMinDay))
			.replace('{maxDay}', String(shippingMaxDay))}`
	}

	return `${props.pageContent.detail_item_estimated_shipping_date_lack_min_or_max.replace(
		'{days}',
		String(shippingMinDay || shippingMaxDay),
	)}`
})

const contentInDynamicLanguage = computed(() => {
	return {
		name: isDisplayLocalLanguage.value
			? dataMercariDetail.value?.localization?.name || ''
			: dataMercariDetail.value?.productInfo.name || '',
		listIntro: [
			{
				title: props.pageContent.detail_item_subtitle_condition,
				content: isDisplayLocalLanguage.value
					? dataMercariDetail.value?.localization?.itemCondition || ''
					: dataMercariDetail.value?.productInfo.itemCondition || '',
			},
			{
				title: props.pageContent.detail_item_subtitle_brand,
				content: dataMercariDetail.value?.productInfo.brand || '',
			},
			{
				title: props.pageContent.detail_item_subtitle_category,
				content: isDisplayLocalLanguage.value
					? dataMercariDetail.value?.localization?.category || ''
					: dataMercariDetail.value?.productInfo.category || '',
			},
			{
				title: props.pageContent.detail_item_subtitle_shipping_paid_by,
				content: isDisplayLocalLanguage.value
					? dataMercariDetail.value?.localization?.shippingPayer || ''
					: dataMercariDetail.value?.productInfo.shippingPayer || '',
				tooltip: props.pageContent.detail_tooltip_shipping_paid_by,
				action: handleGAEventShippingInfo,
			},
			{
				title: props.pageContent.detail_item_subtitle_estimated_shipping_date,
				content: contentShippingDays.value,
				tooltip: props.pageContent.detail_tooltip_estimated_shipping_date,
				action: handleGAEventETAInfo,
			},
		],
		description: isDisplayLocalLanguage.value
			? dataMercariDetail.value?.localization?.description || ''
			: dataMercariDetail.value?.productInfo.description || '',
	}
})

const isShowOriginalPrice = computed(() => {
	return (
		currentDetailType.value === MercariProductTypeEnum.DISCOUNT ||
		currentDetailType.value === MercariProductTypeEnum.SALE
	)
})

const handleGAEventShippingInfo = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'Shipping Paid By Info',
	})
}

const handleGAEventETAInfo = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'ETA Shipping Date Info',
	})
}

const handleGAEventFeeInfo = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'Fee Details Info',
	})
}

const idMercari = computed(() => {
	if (import.meta.env.PROD) {
		return window.location.pathname.split('/').filter(Boolean).pop() || ''
	}

	return new URLSearchParams(window.location.search).get('id') || ''
})

const urlSiffixBuyforyouNew = computed(() => {
	if (!idMercari.value) {
		return ''
	}
	const query = new URLSearchParams(window.location.search)
	const isProdPreview = query.get('_prod') === 'true'
	return `/buyforyou/new?mercariId=${idMercari.value}${
		isProdPreview ? '&_prod=true' : ''
	}`
})

const currentButtonBuyText = computed(() => {
	return currentDetailType.value === MercariProductTypeEnum.SOLD_OUT
		? props.pageContent.detail_button_sold_out
		: props.pageContent.detail_button_buy_now
})

const jsonLdFormat = computed(() => {
	if (!dataMercariDetail.value) {
		return ''
	}

	const { productInfo } = dataMercariDetail.value
	const {
		name,
		description,
		price,
		currency,
		brand,
		status,
		images,
		color,
		size,
		category,
	} = productInfo

	const availability = status === 'on_sale' ? 'InStock' : 'SoldOut'

	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name,
		description,
		url: getMercariUrl('detail', {
			id: idMercari.value,
		}),
		image: images,
		offers: {
			'@type': 'Offer',
			availability: `http://schema.org/${availability}`,
			...(price && {
				price,
			}),
			...(currency && {
				priceCurrency: currency || 'JPY',
			}),
		},
		...(brand && {
			brand: {
				'@type': 'Brand',
				name: brand,
			},
		}),
		...(color && {
			color,
		}),
		...(size && {
			size,
		}),
		...(category && {
			category,
		}),
	}

	try {
		return JSON.stringify(jsonLd)
	} catch (error) {
		console.error('JSON-LD transform fail:', error)
		return ''
	}
})

function createUrlBuyforyouOrder(type: 'root' | 'path') {
	if (!idMercari.value) {
		return ''
	}

	const urlPrefix =
		type === 'path'
			? window.BUYANDSHIP.settings.member_base_path
			: getMcrRootUrl()
	return `${urlPrefix}${urlSiffixBuyforyouNew.value}`
}

function showErrorLayout() {
	isServerError.value = true
}

function handleAsyncTranslationUpdate(
	updatedData: ContentApiMercariProductDetail,
) {
	const currentData = dataMercariDetail.value
	if (!currentData) return

	if (updatedData.localization) {
		currentData.localization = updatedData.localization
		hasTranslationData.value = true
		isShowLocalLanguage.value = true
	}
}

function scheduleAsyncTranslation(id: string, backoffSeconds: number) {
	if (translationTimer.value) {
		clearTimeout(translationTimer.value)
	}

	translationTimer.value = setTimeout(async () => {
		try {
			const updatedResult = await fetchMercariProductDetail(
				id,
				TranslateMode.SYNC,
			)
			handleAsyncTranslationUpdate(updatedResult)
		} catch (error) {
			console.error('Background fetch failed:', error)
		} finally {
			translationTimer.value = null
		}
	}, backoffSeconds * 1000)
}

async function initPage() {
	if (!idMercari.value) {
		showErrorLayout()
		return
	}

	isLoadingBreadcrumbs.value = true

	try {
		createGAEvent(GaGeneralEvent.PageLoad, {
			page_name: GaGeneralPageName.MercariDetail,
		})

		const data = await fetchMercariProductDetail(
			idMercari.value,
			TranslateMode.ASYNC,
		)
		dataMercariDetail.value = data

		if (data.localization) {
			hasTranslationData.value = true
			isShowLocalLanguage.value = true
		} else if (data.translationAsyncBackoff) {
			scheduleAsyncTranslation(idMercari.value, data.translationAsyncBackoff)
		}

		// we don't need to wait this
		handleBuildBreadcrumbs()

		createGAEvent('view_item', {
			page_name: GaGeneralPageName.MercariDetail,
			item_id: `product_${idMercari.value}`,
			item_name: dataMercariDetail.value.productInfo.name,
			item_category: 'Mercari',
		})
	} catch (error) {
		showErrorLayout()
	} finally {
		isLoading.value = false
	}

	createCheckoutShippingHowItWorksView()
}

const prefixBreadcrumbs = computed(() => {
	return [
		{
			displayName: props.pageContent.breadcrumb_home ?? '',
			categoryId: 'home',
			link: getSiteBaseUrl(),
		},
		{
			displayName: props.pageContent.breadcrumb_shopping ?? '',
			categoryId: 'shoppingGuide',
			link: `${getSiteBaseUrl()}/${getSeoRedirectLink('proxyShopping')}/`,
		},
	]
})

async function handleBuildBreadcrumbs() {
	try {
		const isSeoEnhancement = checkIsSeoEnhancement()
		const categoryId = dataMercariDetail.value?.productInfo?.categoryId ?? ''
		const category = dataMercariDetail.value?.localization?.category ?? ''

		const temp = await buildCategoryBreadcrumbs(
			String(categoryId),
			category,
			props.pageContent.detail_button_back_to_mercari,
		)

		breadcrumbs.value = [
			...(isSeoEnhancement ? prefixBreadcrumbs.value : []),
			...temp,
		]
	} catch (error) {
		console.error('Failed to build breadcrumbs:', error)
	} finally {
		isLoadingBreadcrumbs.value = false
	}
}

function toggleLocalLanguage() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: isShowLocalLanguage.value ? 'Show Original' : 'Translate',
	})

	isShowLocalLanguage.value = !isShowLocalLanguage.value
}

function hideDialogHowItWork() {
	isShowDialogHowItWork.value = false
}

function showDialogHowItWork() {
	isShowDialogHowItWork.value = true

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'Tutorial Shipping Process',
	})
}

function redirectShipmentProtection() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'Tutorial Shopping Protection',
	})

	const suffix = isRegionSupportBuyforyou.value ? '#b4u' : ''
	window.open(`${getSiteBaseUrl()}/shipment-protection/${suffix}`)
}

function initWebComponent() {
	const webcomponent: any = document.createElement(
		WebComponentNameList.CheckoutShippingHowItWorksView,
	)

	webcomponent.promise.then((title: string) => {
		isWebComponentLoading.value = false
		dialogHowItWorkTitle.value = title
	})
}

function handleClickBuy() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'Buy Now',
		item_id: `product_${idMercari.value}`,
		item_name: dataMercariDetail.value?.productInfo.name,
		item_category: 'Mercari',
	})
	if (isLoginGlobal.value) {
		window.location.href = createUrlBuyforyouOrder('root')
	} else {
		window.BsHeaderVm.showAuthPanel()
	}
}

function showDialogFeeDetails() {
	handleGAEventFeeInfo()
	isShowDialogFeeDetails.value = true
}

function hideDialogFeeDetails() {
	isShowDialogFeeDetails.value = false
}

function reloadPage() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariDetail,
		button_name: 'Try Again',
	})
	window.location.reload()
}

watch(isShowDialogHowItWork, value => {
	if (value) {
		initWebComponent()
	}
})

watch(isSmall, value => {
	if (!value) {
		hideDialogFeeDetails()
	}
})

watch(isLoading, value => {
	if (!value) {
		if (window.BsHeaderVm) {
			window.BsHeaderVm.setTypeSuccessDialog('b4u')
			if (
				window.BsHeaderVm.isNeedTriggerActionButton &&
				window.BsHeaderVm.typeSuccessDialog === 'b4u'
			) {
				window.location.href = createUrlBuyforyouOrder('root')
			}
		}
	}
})

onUnmounted(() => {
	if (translationTimer.value) {
		clearTimeout(translationTimer.value)
		translationTimer.value = null
	}
})

initPage()
</script>

<template>
	<div>
		<template v-if="!isServerError">
			<div v-if="isSmall && !isLoading" class="relative">
				<div class="px-20px py-16px mdp:p-0 mdp:pb-[30px] lg:-mt-[30px]">
					<Skeleton
						v-if="isLoadingBreadcrumbs"
						class="h-[24px] w-3/5"
					></Skeleton>
					<MercariDetailCategoryBreadcrumb :breadcrumbs="breadcrumbs" />
				</div>

				<GallerySlideshow :mediaList="mediaList"></GallerySlideshow>

				<div class="p-20px">
					<MercariDetailProductName
						:titleMercari="pageContent.detail_type_mercari_title"
						:productName="contentInDynamicLanguage.name"
						:isLoading="isLoading"
					/>

					<Skeleton
						class="mt-16px h-24px w-[120px]"
						v-if="isLoading"
					></Skeleton>

					<div class="mt-16px" v-if="isShowTag && tagText">
						<MercariDetailTag :text="tagText" :type="currentDetailType" />
					</div>
					<MercariDetailTranslation
						v-if="isTranslateSectionShow"
						:title="currentTranslateSectionContent.title"
						:buttonText="currentTranslateSectionContent.button"
						@translate="toggleLocalLanguage"
					/>
					<MercariDetailInfo
						v-if="!isLoading"
						:pageContent="pageContent"
						:listIntro="contentInDynamicLanguage.listIntro"
						:description="contentInDynamicLanguage.description"
					/>

					<MercariDetailSeller
						v-if="
							!isLoading && dataMercariDetail && dataMercariDetail.sellerInfo
						"
						:pageContent="pageContent"
						:sellerInfo="dataMercariDetail.sellerInfo"
					/>
					<MercariDetailShoppingDetail
						v-if="!isLoading"
						:pageContent="pageContent"
						:listShoppingDetail="listShoppingDetail"
					/>
				</div>
				<div
					v-if="!isLoading"
					class="sticky bottom-0 z-[1] mt-[80px] border-t-[1px] border-solid border-n-100 bg-white px-20px pb-[36px] pt-16px"
				>
					<div class="mb-8px flex items-center justify-between">
						<div
							class="flex items-center gap-4px"
							@click="showDialogFeeDetails"
						>
							<p class="typography-s2-reg text-n-500">
								{{ pageContent.detail_fee_details }}
							</p>
							<div class="h-16px w-16px">
								<BaseIcon
									name="suggest-Info"
									class="h-full w-full fill-n-500"
								/>
							</div>
						</div>
						<div
							class="ml-16px flex flex-1 flex-wrap-reverse items-center justify-end"
						>
							<p
								class="typography-s2-reg text-n-500 line-through"
								v-if="isShowOriginalPrice"
							>
								{{ originalPriceWithCurrency }}
							</p>
							<p class="typography-h4-str ml-8px text-o-500">
								{{ finalPriceWithCurrency }}
							</p>
						</div>
					</div>
					<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
					<BaseButton
						id="success-dialog-primary-button"
						:isFull="true"
						:size="'large'"
						:isDisabled="currentDetailType === MercariProductTypeEnum.SOLD_OUT"
						@click="handleClickBuy"
					>
						{{ currentButtonBuyText }}
					</BaseButton>
				</div>
			</div>

			<div v-if="!isSmall">
				<div class="bs-container-no-grid pb-72px pt-[30px] lg:pt-[60px]">
					<div class="px-20px py-16px mdp:p-0 mdp:pb-[30px] lg:-mt-[30px]">
						<Skeleton
							v-if="isLoadingBreadcrumbs"
							class="h-[24px] w-1/3"
						></Skeleton>
						<MercariDetailCategoryBreadcrumb :breadcrumbs="breadcrumbs" />
					</div>

					<div class="flex flex-wrap gap-[30px] lg:gap-[52px]">
						<div class="flex-[1.8]">
							<GallerySlideshow :mediaList="mediaList"></GallerySlideshow>

							<MercariDetailTranslation
								v-if="isTranslateSectionShow"
								:title="currentTranslateSectionContent.title"
								:buttonText="currentTranslateSectionContent.button"
								@translate="toggleLocalLanguage"
							/>

							<MercariDetailInfo
								v-if="!isLoading"
								:pageContent="pageContent"
								:listIntro="contentInDynamicLanguage.listIntro"
								:description="contentInDynamicLanguage.description"
							/>

							<MercariDetailSeller
								v-if="
									!isLoading &&
									dataMercariDetail &&
									dataMercariDetail.sellerInfo
								"
								:pageContent="pageContent"
								:sellerInfo="dataMercariDetail.sellerInfo"
							/>
						</div>

						<div class="relative flex-1">
							<div class="sticky top-[92px]">
								<MercariDetailProductName
									:titleMercari="pageContent.detail_type_mercari_title"
									:productName="contentInDynamicLanguage.name"
									:isLoading="isLoading"
								/>

								<div class="mt-16px">
									<template v-if="isLoading">
										<Skeleton class="mt-16px h-[30px] w-[180px]"></Skeleton>
									</template>
									<template v-else>
										<div class="mb-4px flex" v-if="isShowTag">
											<MercariDetailTag
												:text="tagText"
												:type="currentDetailType"
											/>
											<div
												class="typography-b2-reg ml-12px text-n-500 line-through"
												v-if="isShowOriginalPrice"
											>
												{{ originalPriceWithCurrency }}
											</div>
										</div>

										<p class="typography-h4-str text-o-500">
											{{ finalPriceWithCurrency }}
										</p>
									</template>
								</div>

								<div class="mt-24px">
									<Skeleton class="h-[52px] w-full" v-if="isLoading"></Skeleton>
									<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
									<BaseButton
										v-else
										id="success-dialog-primary-button"
										:isFull="true"
										:size="'large'"
										:isDisabled="
											currentDetailType === MercariProductTypeEnum.SOLD_OUT
										"
										@click="handleClickBuy"
									>
										{{ currentButtonBuyText }}
									</BaseButton>
								</div>

								<div class="mt-casual-friends">
									<div v-if="isLoading" class="flex flex-wrap gap-8px">
										<template v-for="i in 2">
											<Skeleton class="h-24px w-full basis-full"></Skeleton>
										</template>
										<Skeleton class="h-24px w-[180px]"></Skeleton>
									</div>
									<template v-else>
										<p class="typography-s2-emp mb-4px text-n-950">
											{{ pageContent.detail_fee_details }}
										</p>
										<p
											class="typography-c1-reg whitespace-pre-line text-[#262626]"
										>
											{{ pageContent.detail_fee_details_notice }}
										</p>
									</template>
								</div>

								<template v-if="!isLoading">
									<div class="my-casual-friends h-[1px] w-full bg-n-100"></div>

									<MercariDetailShoppingDetail
										:pageContent="pageContent"
										:listShoppingDetail="listShoppingDetail"
									/>
								</template>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>

		<div class="bs-container-no-grid py-[120px]" v-else>
			<div class="mx-auto flex max-w-[300px] flex-col items-center gap-32px">
				<div class="h-[150px] w-[150px]">
					<img
						class="w-full"
						:srcset="`
								${getVueAssetImagePath('mercari-product-list-server-error.png')}
							`"
						alt="mercari product list server-error"
					/>
				</div>

				<div class="flex flex-col items-center gap-16px">
					<div class="flex flex-col gap-12px">
						<p class="typography-h4-str text-center text-n-950">
							{{ pageContent.detail_edge_case_server_error_title }}
						</p>
						<p class="typography-s1-reg text-center text-n-400">
							{{ pageContent.detail_edge_case_server_error_description }}
						</p>
					</div>

					<div class="w-auto">
						<BaseButton variant="primary" size="medium" @click="reloadPage">
							{{ pageContent.detail_edge_case_server_error_button_text }}
						</BaseButton>
					</div>
				</div>
			</div>
		</div>

		<DialogFull
			v-model="isShowDialogHowItWork"
			:isForWebComponentShipping="true"
			@on-hide="hideDialogHowItWork"
		>
			<template #content>
				<div
					class="mb-32px hidden pl-16px text-[24px] leading-[32px] tracking-normal text-n-950 lg:block"
				>
					{{ dialogHowItWorkTitle }}
				</div>
				<HowItWorksSkeleton v-if="isWebComponentLoading"></HowItWorksSkeleton>
				<web-component-checkout-shipping-how-it-works-view
					v-show="!isWebComponentLoading"
				></web-component-checkout-shipping-how-it-works-view>
			</template>
		</DialogFull>

		<DialogRevamp
			v-model="isShowDialogFeeDetails"
			size="small"
			:isShowDefaultClose="true"
		>
			<template #header>
				<p class="typography-h5-str text-n-950">
					{{ pageContent.detail_fee_details }}
				</p>
			</template>
			<template #content>
				<div class="typography-b2-reg whitespace-pre-line text-n-950">
					{{ pageContent.detail_fee_details_notice }}
				</div>
			</template>
		</DialogRevamp>

		<Teleport to="head" v-if="jsonLdFormat">
			<component :is="'script'" type="application/ld+json">
				{{ jsonLdFormat }}
			</component>
		</Teleport>
	</div>
</template>
